// src/pages/services/ITservices/SocialMediaMarketing/SocialMediaMarketing.js

import React, { useEffect, useRef, useState } from "react";
import "./SocialMediaMarketing.css";
import { 
  Target, Sparkles, DollarSign, Search, BarChart3, 
  Users, UserCheck, Smartphone 
} from "lucide-react";

// Inline brand icon SVGs to replace deprecated Lucide brand icons
const InstagramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const TwitterIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
  </svg>
);

const YoutubeIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.41 19c1.71.46 8.59.46 8.59.46s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96 29 29 0 0 0 .46-5.29 29 29 0 0 0-.46-5.33z" />
    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
  </svg>
);

// ─── DATA ────────────────────────────────────────────────────────────────────

const servicesData = [
  {
    id: 1,
    icon: "Target",
    title: "Social Media Strategy",
    desc: "Personalized campaigns tailored to your brand and target audience. We analyze competitors, define your voice, and map out a content calendar that keeps your audience coming back.",
    tag: "Your Vision, Our Strategy",
    delay: "0ms",
  },
  {
    id: 2,
    icon: "Sparkles",
    title: "Creative Content Production",
    desc: "Scroll-stopping graphics, reels, carousels, and copy that reflect your brand perfectly. Creativity that performs — not just looks good, but drives measurable action.",
    tag: "Creativity That Performs",
    delay: "100ms",
  },
  {
    id: 3,
    icon: "DollarSign",
    title: "Paid Advertising (PPC)",
    desc: "Meta Ads, Google Ads, and LinkedIn campaigns optimized for maximum ROAS. Full-funnel strategies covering awareness, engagement, and conversion for every business stage.",
    tag: "Full-Funnel Ads",
    delay: "200ms",
  },
  {
    id: 4,
    icon: "Search",
    title: "SEO & Content Marketing",
    desc: "Long-form content, keyword strategy, and technical SEO that builds organic authority over time. Assets that compound in value and keep driving traffic for years.",
    tag: "Organic Growth",
    delay: "300ms",
  },
  {
    id: 5,
    icon: "BarChart3",
    title: "Analytics & Reporting",
    desc: "Transparent reporting, real-time dashboards, and a dedicated account manager. You always know what's working, what's not, and exactly where your budget is going.",
    tag: "Transparent Reporting",
    delay: "400ms",
  },
  {
    id: 6,
    icon: "Users",
    title: "Influencer & Community",
    desc: "We connect your brand with the right creators and build loyal communities. From micro-influencers to large-scale partnerships — authentic and ROI-driven.",
    tag: "Brand Advocacy",
    delay: "500ms",
  },
];

const barData = [
  { height: "35%", color: "rgba(247,201,72,0.4)",  day: "M" },
  { height: "55%", color: "rgba(247,201,72,0.52)", day: "T" },
  { height: "45%", color: "rgba(247,201,72,0.62)", day: "W" },
  { height: "70%", color: "rgba(247,201,72,0.74)", day: "T" },
  { height: "60%", color: "rgba(247,201,72,0.85)", day: "F" },
  { height: "85%", color: "#f7c948",               day: "S" },
  { height: "75%", color: "#00e5c3",               day: "S" },
];

const platformsData = [
  { icon: "Instagram", name: "Instagram", stat: "+89K" },
  { icon: "Facebook", name: "Facebook",  stat: "+64K" },
  { icon: "Twitter", name: "Twitter",   stat: "+31K" },
  { icon: "Youtube", name: "YouTube",   stat: "+12K" },
];

const reasonsData = [
  {
    icon: "Target",
    title: "Your Vision, Our Strategy",
    desc: "Every campaign is anchored to your specific business goals — not vanity metrics. We start with where you want to go.",
  },
  {
    icon: "Sparkles",
    title: "Creativity That Performs",
    desc: "Our creative team doesn't just make pretty things. Everything is tested, iterated, and optimized to actually drive results.",
  },
  {
    icon: "UserCheck",
    title: "Dedicated Support & Account Manager",
    desc: "A real person who knows your brand, answers your questions, and proactively looks for growth opportunities every week.",
  },
  {
    icon: "BarChart3",
    title: "Transparent Reporting & Analytics",
    desc: "Monthly performance reviews, live dashboards, and honest conversations about what's working and how we're improving.",
  },
];

const resultsData = [
  { num: "3×",   label: "Average ROI increase within 90 days", full: true },
  { num: "500+", label: "Brands Grown", full: false },
  { num: "98%",  label: "Client Retention", full: false },
  { num: "2.4M", label: "Monthly impressions managed across all clients", full: true },
];

const iconMap = {
  Target: <Target size={24} />,
  Sparkles: <Sparkles size={24} />,
  DollarSign: <DollarSign size={24} />,
  Search: <Search size={24} />,
  BarChart3: <BarChart3 size={24} />,
  Users: <Users size={24} />,
  UserCheck: <UserCheck size={24} />,
  Instagram: <InstagramIcon size={20} />,
  Facebook: <FacebookIcon size={20} />,
  Twitter: <TwitterIcon size={20} />,
  Youtube: <YoutubeIcon size={20} />,
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────

const SocialMediaMarketing = () => {
  const sectionRef  = useRef(null);
  const barsRef     = useRef(null);
  const [barsAnimated, setBarsAnimated] = useState(false);

  // ── Animate bars when dashboard scrolls into view ──
  useEffect(() => {
    if (!barsRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !barsAnimated) {
            setBarsAnimated(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(barsRef.current);
    return () => observer.disconnect();
  }, [barsAnimated]);

  // ── Scroll-in animation for cards / reasons / results ──
  useEffect(() => {
    const targets = sectionRef.current
      ? sectionRef.current.querySelectorAll(
          ".smm-reason, .smm-result-card, .smm-plat-item, .smm-dash-card"
        )
      : [];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    targets.forEach((el, i) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(22px)";
      el.style.transition = `opacity 0.5s ease ${i * 80}ms, transform 0.5s ease ${i * 80}ms`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // ── Metric live flicker simulation ──
  const [reachVal, setReachVal] = useState("+127%");
  useEffect(() => {
    const timer = setInterval(() => {
      const base = 127 + Math.floor(Math.random() * 5 - 2);
      setReachVal(`+${base}%`);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  // ── Scroll helpers ──
  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToServices = () => {
    const el = document.getElementById("services");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="social-media-marketing" className="smm-section standalone-page" ref={sectionRef}>

      {/* ── Background ── */}
      <div className="smm-bg" />
      <div className="smm-grid-lines" />
      <div className="smm-blob smm-blob-1" />
      <div className="smm-blob smm-blob-2" />
      <div className="smm-blob smm-blob-3" />
      <div className="smm-rings">
        <div className="smm-ring smm-ring-1" />
        <div className="smm-ring smm-ring-2" />
        <div className="smm-ring smm-ring-3" />
      </div>

      <div className="smm-inner">

        {/* ── Section Header ── */}
        <div className="smm-header">
          <span className="smm-badge" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Sparkles size={14} />
            Social Media Marketing
          </span>
          <h2 className="smm-section-title">
            Grow Your Business with{" "}
            <span className="smm-title-accent">Smart Marketing Strategies</span>
          </h2>
          <p className="smm-section-sub">
            Full-stack digital marketing solutions designed to attract, engage, and convert.
            Whether you're a startup or a well-established brand, our team crafts personalized
            campaigns that bring real business growth.
          </p>
          <div className="smm-header-line" />
        </div>

        {/* ── Services Grid ── */}
        <div className="smm-services-grid">
          {servicesData.map((svc) => (
            <div
              key={svc.id}
              className="smm-svc-card"
              style={{ animationDelay: svc.delay }}
            >
              <div className="smm-svc-icon-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#f7c948" }}>
                {iconMap[svc.icon]}
              </div>
              <h3 className="smm-svc-title">{svc.title}</h3>
              <p className="smm-svc-desc">{svc.desc}</p>
              <span className="smm-svc-tag">{svc.tag}</span>
            </div>
          ))}
        </div>

        {/* ── Analytics Dashboard ── */}
        <div className="smm-dashboard" ref={barsRef}>

          {/* Analytics Card */}
          <div className="smm-dash-card">
            <div className="smm-dash-header">
              <span className="smm-dash-title" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <BarChart3 size={16} /> Campaign Analytics
              </span>
              <span className="smm-dash-period">Last 30 Days</span>
            </div>
            <div className="smm-metrics">
              <div className="smm-metric">
                <div className="smm-metric-val yellow">{reachVal}</div>
                <div className="smm-metric-label">Reach Growth</div>
                <div className="smm-metric-change">↑ vs last month</div>
              </div>
              <div className="smm-metric">
                <div className="smm-metric-val pink">4.8×</div>
                <div className="smm-metric-label">ROAS</div>
                <div className="smm-metric-change">↑ improving</div>
              </div>
              <div className="smm-metric">
                <div className="smm-metric-val teal">2.4M</div>
                <div className="smm-metric-label">Impressions</div>
                <div className="smm-metric-change">↑ record high</div>
              </div>
            </div>
            <div className="smm-bar-chart">
              {barData.map((bar, i) => (
                <div
                  key={i}
                  className="smm-bar"
                  style={{
                    height: barsAnimated ? bar.height : "0%",
                    background: bar.color,
                    transitionDelay: `${i * 80}ms`,
                  }}
                />
              ))}
            </div>
            <div className="smm-bar-labels">
              {barData.map((bar, i) => (
                <span key={i} className="smm-bar-label">{bar.day}</span>
              ))}
            </div>
          </div>

          {/* Platforms Card */}
          <div className="smm-dash-card">
            <div className="smm-dash-header">
              <span className="smm-dash-title" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <Smartphone size={16} /> Active Platforms
              </span>
              <span className="smm-dash-period">Live</span>
            </div>
            <div className="smm-platforms-row">
              {platformsData.map((p, i) => (
                <div key={i} className="smm-plat-item">
                  <div className="smm-plat-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#f7c948" }}>
                    {iconMap[p.icon]}
                  </div>
                  <div className="smm-plat-name">{p.name}</div>
                  <div className="smm-plat-stat">{p.stat}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Why Choose Us ── */}
        <div className="smm-why">
          <div className="smm-why-grid">

            {/* Left: Reasons */}
            <div>
              <p className="smm-why-eyebrow">Why Choose Us</p>
              <h3
                className="smm-section-title"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)" }}
              >
                Because We Deliver{" "}
                <span className="smm-title-accent">Results, Not Excuses</span>
              </h3>
              <div className="smm-reasons">
                {reasonsData.map((r, i) => (
                  <div key={i} className="smm-reason">
                    <div className="smm-reason-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#f7c948" }}>
                      {iconMap[r.icon]}
                    </div>
                    <div className="smm-reason-body">
                      <h4>{r.title}</h4>
                      <p>{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Results */}
            <div>
              <p className="smm-why-eyebrow">Real Results</p>
              <h3
                className="smm-section-title"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", marginBottom: "32px" }}
              >
                Numbers That{" "}
                <span className="smm-title-accent">Speak for Themselves</span>
              </h3>
              <div className="smm-results">
                {/* Full-width cards */}
                {resultsData.filter((r) => r.full).slice(0, 1).map((r, i) => (
                  <div key={i} className="smm-result-card">
                    <div className="smm-result-num">{r.num}</div>
                    <div className="smm-result-label">{r.label}</div>
                  </div>
                ))}
                {/* 2-column row */}
                <div className="smm-result-row">
                  {resultsData.filter((r) => !r.full).map((r, i) => (
                    <div key={i} className="smm-result-card">
                      <div className="smm-result-num">{r.num}</div>
                      <div className="smm-result-label">{r.label}</div>
                    </div>
                  ))}
                </div>
                {/* Last full-width */}
                {resultsData.filter((r) => r.full).slice(1).map((r, i) => (
                  <div key={i} className="smm-result-card">
                    <div className="smm-result-num">{r.num}</div>
                    <div className="smm-result-label">{r.label}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ── CTA Banner ── */}
        <div className="smm-cta-banner">
          <div>
            <p className="smm-cta-eyebrow">Let's Grow Together</p>
            <h3 className="smm-cta-heading">Ready to Grow Your Brand?</h3>
            <p className="smm-cta-text">
              Whether you're a startup or a well-established brand, we craft personalized
              campaigns that bring real business growth. Let's talk strategy — free consultation,
              no strings attached.
            </p>
          </div>
          <div className="smm-cta-actions">
            <button className="smm-btn-primary" onClick={scrollToContact}>
              Get Free Strategy Call →
            </button>
            <button className="smm-btn-ghost" onClick={scrollToServices}>
              Back to Services
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default SocialMediaMarketing;
